import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, ShieldAlert, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Blocking modal shown when the backend reports `force_password_change=true`.
 * Cannot be dismissed by clicking outside — user must rotate the password
 * before they can use the Studio. On success the parent re-fetches the user.
 */
export default function PasswordChangeGate({ email, onSuccess }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (next.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (next !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (next === current) {
      toast.error('New password must differ from current password');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      toast.success('Password updated. Welcome.');
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
         data-testid="password-change-gate">
      <div className="bg-white rounded-sm border border-black/10 max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 rounded-sm bg-brand-accent/15 flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-brand-accent-dark" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-lg font-medium text-brand-dark">
              Set a new password
            </h2>
            <p className="text-sm font-body text-brand-dark/60 mt-1">
              This is your first sign-in as <strong>{email}</strong>. Please choose
              a strong password before continuing.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="password-change-form">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">
              Current password
            </label>
            <Input type={show ? 'text' : 'password'} value={current}
              onChange={(e) => setCurrent(e.target.value)} autoFocus
              data-testid="pwd-current" className="rounded-sm h-10" required />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">
              New password
            </label>
            <Input type={show ? 'text' : 'password'} value={next}
              onChange={(e) => setNext(e.target.value)}
              data-testid="pwd-new" className="rounded-sm h-10" required minLength={8} />
            <p className="text-[11px] text-brand-dark/40 font-body mt-1">
              At least 8 characters. Use a mix of letters, numbers and symbols.
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">
              Confirm new password
            </label>
            <Input type={show ? 'text' : 'password'} value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="pwd-confirm" className="rounded-sm h-10" required minLength={8} />
          </div>

          <label className="flex items-center gap-2 text-xs font-body text-brand-dark/60 cursor-pointer pt-1">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)}
                   data-testid="pwd-show-toggle" className="h-3.5 w-3.5" />
            {show ? <><EyeOff className="w-3.5 h-3.5" /> Hide passwords</> : <><Eye className="w-3.5 h-3.5" /> Show passwords</>}
          </label>

          <div className="pt-3">
            <Button type="submit" disabled={submitting} data-testid="pwd-submit"
              className="w-full bg-brand-dark text-white hover:bg-gray-800 rounded-sm h-10 font-body text-xs uppercase tracking-wider">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set new password'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
